import { registerConfirmSteps, registerScreenActions } from '../../studio-nav/screen-panels.service';
import type { ScreenActionAnswer } from '../../services/mcp/actions/fast-lane';
import type { Prefilled, Prepared, SaveOutcome } from '../../services/mcp/actions/confirm-lane';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren, ChangeDetectorRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WorkspacesService, Workspace, WorkspaceUser, ProjectSwitches, MobileAccess } from '../../services/workspaces.service';
import { MembersService } from '../../services/members.service';
import { RoleService } from '../../services/role.service';
import {
  Access, Control, projectSwitchControl, auditLine, filterWorkspace, FilteredWorkspace, noSearchResultsMessage,
  shouldShowNoResults, SEARCH_LOAD_INCOMPLETE, matchedUserIds, workspaceTotals, WorkspaceTotals,
  MODE_LABELS, MODE_NOTES, NATIVE_ONLY_WARNING, workspacesInPageOrder, arrivalNoticeOn, deletionReadOnly,
} from '../../services/workspaces.util';
import { WorkspaceRowComponent } from './workspace-row.component';
import { MobileAccessMode } from '../../services/service-worker.util';
import { StudioNavService } from '../../studio-nav/studio-nav.service';

/**
 * Workspaces tab — the top half built here (Task 5): loading the list, and the two project-wide
 * switches that close a project's front door. Task 6 adds the list itself (`WorkspaceRowComponent`
 * per workspace), creating a workspace, and which rows are open.
 */
@Component({
  selector: 'app-workspaces-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkspaceRowComponent],
  templateUrl: './workspaces-tab.component.html',
  styleUrl: './workspaces-tab.component.css',
})
export class WorkspacesTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() access!: Access;
  @Input() search = '';
  /** The no-results state's "clear search" button (Step 3) has nothing of its own to clear —
   *  `search` is an Input, owned by `UsersWorkspacesComponent` — so it asks the shell instead of
   *  reaching past its own Input to mutate state it does not own. */
  @Output() clearSearch = new EventEmitter<void>();

  private ws = inject(WorkspacesService);
  private members = inject(MembersService);
  private roleService = inject(RoleService);
  private nav = inject(StudioNavService);
  private cdr = inject(ChangeDetectorRef);

  loading = signal(true);
  /** A mutation-triggered `reload()`, as opposed to the real first load — kept separate from
   *  `loading` so a rename or a create refreshes the data WITHOUT unmounting the switches panel,
   *  the create card and every row back to the skeleton. `openIds` already survives `reload()`
   *  replacing every `Workspace` object, but re-showing the skeleton on every mutation would still
   *  flash the whole panel — and Tasks 7-10 will make that flash heavier once rows fetch their own
   *  users on mount. Keep using this (not `loading`) for any post-mutation refresh. */
  refreshing = signal(false);
  error = signal('');
  workspaces = signal<Workspace[]>([]);
  switches = signal<ProjectSwitches | null>(null);
  mobileAccess = signal<MobileAccess | null>(null);
  /** Which project switch is mid-flight, so only ITS button shows "Saving…". */
  busy = signal<'' | 'signups' | 'invites' | 'mobileAccess'>('');

  /**
   * Developer mode (fix-round-1 finding 4) — the THIRD door `doorsInto` (workspaces.util.ts) needs
   * for the builders' workspace, alongside `switches` above. Fetched ONCE, in parallel with the
   * first `reload()`, not on every silent reload after a mutation: it's a Zavo-team-only toggle
   * (left-nav.component.ts's own `canSetDeveloperMode`) that nothing on THIS tab can change, so
   * re-fetching it on every rename/remove/add would just be an extra `getHosting()` round trip for
   * a fact that never moved. Defaults false (off) until the first answer lands — `doorsInto` already
   * treats a missing value as off, not "unknown", so this is the safe default rather than a stale one.
   */
  developerModeOn = signal(false);

  /**
   * Role id → role NAME, handed to every row so a person's pill reads "Sales Executive L1" rather
   * than the stored id `sale_executive_l1` (`roleName`, workspaces.util.ts). Fetched ONCE, in parallel
   * with the first `reload()`, for the same reason `developerModeOn` is: nothing on this tab creates or
   * renames a role, so re-fetching on every mutation would buy nothing. Empty until it lands — and if
   * it never does, every pill simply shows the id it always showed.
   */
  roleNames = signal<ReadonlyMap<string, string>>(new Map());

  /**
   * Which workspace rows are expanded. A `Set<string>` rather than one index (the shape
   * `pages.component.ts`'s `expandedIndex` uses) because several rows must stay open at once —
   * this is the single source of truth `WorkspaceRowComponent` renders from (`open`); the row
   * itself holds no open/closed state of its own, so it survives `reload()` replacing every
   * `Workspace` object with a fresh one from the server.
   */
  openIds = signal<Set<string>>(new Set());

  /**
   * Nav 7b final review (I7) — "open the Sales workspace" while this tab is already showing: no reload comes to take
   * the note, so this does — once the list has loaded and has that workspace. A note that arrives with the tab
   * (the list still empty) is left for `reload()`, which takes it at its start.
   */
  private navSub = this.nav.intent$.subscribe((i) => {
    if (i?.kind !== 'workspace' || this.loading() || !this.workspaces().some((w) => w.id === i.id)) return;
    this.nav.take('workspace');
    this.openIds.update((s) => new Set([...s, i.id]));
  });

  /**
   * Task 11's search results, one `FilteredWorkspace` per workspace id — populated only while
   * `search` is non-empty (see `applySearch()`). Empty (no search yet, or the box is empty) reads
   * through `isVisible`/`countLabelFor` as "show everything, no count", the same shape
   * `filterWorkspace` itself returns for an empty term — so there is no separate "no search active"
   * branch to keep in sync with that contract.
   */
  private filtered = signal<Map<string, FilteredWorkspace>>(new Map());
  searching = signal(false);
  /** True while any workspace's `listUsers` failed during the CURRENT search pass — fix-round-1
   *  Critical 2. Drives both `noResults()` (via `shouldShowNoResults`, which refuses to claim a
   *  confident "nothing matches" here) and the banner in the template; reset to `false` the moment
   *  the term is cleared or a fresh search pass starts clean. */
  loadFailed = signal(false);
  /** WHICH workspaces those were — fix-round-3 Critical 2. `loadFailed` above answers "was the page's
   *  banner earned"; this answers "is THIS row one of the ones it is about", which is what each row
   *  needs in order to say so itself instead of silently claiming nobody in it matched. Kept beside
   *  `loadFailed` and cleared in exactly the same places, so the banner and the rows can never
   *  disagree about whether a search pass was complete. */
  private failedIds = signal<Set<string>>(new Set());
  /** "Newest search wins" — the same idiom `WorkspaceRowComponent.loadUsers` already uses via its
   *  own `loadSeq` (see that method's comment). Bumped at the START of every `applySearch()` call,
   *  including the empty-term branch, so a slow response for an abandoned term can never resurrect
   *  itself after a newer term (or a clear) has already taken over — fix-round-1 Critical 1. */
  private searchSeq = 0;

  newWorkspaceName = '';
  creatingWorkspace = signal(false);
  createError = signal('');
  private offers = registerScreenActions({
    'create-workspace': (values) => this.createWorkspaceFor(values),
    'reload-list': async () => {
      // Was (2026-10-30): await this.reload(); The loud reload swaps the rows for the skeleton, destroying what is typed in an open one.
      await this.reload({ silent: true });
      const failed = this.error();
      if (failed) throw new Error(failed);
      return { done: 'Reloaded the workspaces.', result: { shown: this.workspaces().length } };
    },
    'expand-workspace-row': (values) => this.onRowFast(values, async (row) => {
      const was = this.isOpen(row.workspace.id);
      await this.openRow(row);
      return { done: was ? `${row.workspace.name} was already open.` : `Opened ${row.workspace.name}.`, result: { workspaceId: row.workspace.id } };
    }),
    'rename-workspace': (values) => this.onRowFast(values, (row) => row.renameFor(values['name'])),
    'set-workspace-arrival-notices': (values) => this.onRowFast(values, (row) => row.arrivalsFor(values['enabled'])),
    'set-person-name': (values) => this.onPerson(values, (row, u) => row.personNameFor(u, values['name'])),
  });
  private confirmSteps = registerConfirmSteps({
    'set-project-invites': { overlay: (values) => this.prepareProjectSwitch('invites', values['enabled']) },
    'set-project-signups': { overlay: (values) => this.prepareProjectSwitch('signups', values['enabled']) },
    'set-mobile-access-mode': { overlay: (values) => this.prepareMode(values['mode']) },
    'set-workspace-invites': { overlay: (values) => this.onRow(values, (row) => row.prepareSwitch('invites', values['enabled'])) },
    'set-workspace-adds': { overlay: (values) => this.onRow(values, (row) => row.prepareSwitch('adds', values['enabled'])) },
    'resend-invite': { overlay: (values) => this.onRow(values, (row) => row.prepareResend(values['inviteId'])) },
    'cancel-invite': { overlay: (values) => this.onRow(values, (row) => row.prepareCancel(values['inviteId'])) },
    'set-seat-cap': { press: (values) => this.onOpenRow(values, (row) => row.prefillSeatCap(values)) },
    'add-person': { press: (values) => this.onOpenRow(values, (row) => row.prefillAdd(values)) },
    'restore-workspace': { press: (values) => this.onRow(values, (row) => row.prefillRestore()) },
    'change-person-role': { overlay: (values) => this.onPerson(values, (row, u) => row.prepareRole(u, values['roleId'])) },
    'set-person-password': { press: (values) => this.onPerson(values, (row, u) => row.prefillPassword(u, values['password'])) },
    'set-person-sign-in-email': { press: (values) => this.onPerson(values, (row, u) => row.prefillEmail(u, values['email'])) },
    'remove-person': { press: (values) => this.onPerson(values, (row, u) => row.prefillRemove(u)) },
    'transfer-person': { press: (values) => this.onPerson(values, (row, u) => row.prefillTransfer(u, values['toPersonId'], values['entityIds'])) },
    'erase-person': { press: (values) => this.onPerson(values, (row, u) => row.prefillErase(u, values['confirmText'])) },
    'reinstate-person': { press: (values) => this.onPerson(values, (row, u) => row.prefillReinstate(u, values['roleId'], values['password'])) },
  });

  @ViewChildren(WorkspaceRowComponent) private rows?: QueryList<WorkspaceRowComponent>;

  /** A row is only drawn while a search hasn't hidden it, and the row holds its switches and invitations. */
  // Was (2026-10-30): const row = this.rows?.find((r) => r.workspace.id === values['workspaceId']); and only ever Prepared.
  private async onRow<T>(values: Record<string, unknown>, then: (row: WorkspaceRowComponent) => T | Promise<T>): Promise<T | { refused: string }> {
    const row = this.rowNamed(values['workspaceId']);
    if ('refused' in row) return row;
    // A wait that ran out told no row, and a tag left behind would shadow the next one: the overlay marks the first it finds.
    this.rows?.forEach((r) => r.stopMarking());
    return then(row);
  }

  private onRowFast(values: Record<string, unknown>, then: (row: WorkspaceRowComponent) => ScreenActionAnswer | Promise<ScreenActionAnswer>): Promise<ScreenActionAnswer> {
    const row = this.rowNamed(values['workspaceId']);
    return 'refused' in row ? Promise.resolve(row) : Promise.resolve(then(row));
  }

  /** The id read_screen gives, or the name the builder uses. A name two rows share has to be settled by id. */
  private rowNamed(wanted: unknown): WorkspaceRowComponent | { refused: string } {
    const rows = this.rows?.toArray() ?? [];
    const byId = rows.find((r) => r.workspace.id === wanted);
    if (byId) return byId;
    const name = String(wanted ?? '').trim().toLowerCase();
    const named = rows.filter((r) => name && r.workspace.name.trim().toLowerCase() === name);
    if (named.length > 1) return { refused: `More than one workspace is called ${String(wanted)}; use its id from read_screen. Nothing was changed.` };
    return named[0] ?? { refused: 'That workspace isn\'t on the builder\'s screen: it doesn\'t exist, or a search is hiding it. Nothing was changed.' };
  }

  /** The row's body and its people are drawn only once it is open, and the forms live in that body. */
  private async openRow(row: WorkspaceRowComponent): Promise<void> {
    if (!this.isOpen(row.workspace.id)) this.toggleOpen(row.workspace.id);
    this.cdr.detectChanges();
    await row.loadUsers(true);
    this.cdr.detectChanges();
  }

  private onOpenRow(values: Record<string, unknown>, then: (row: WorkspaceRowComponent) => Prefilled | Promise<Prefilled>): Promise<Prefilled> {
    return this.onRow(values, async (row) => {
      await this.openRow(row);
      return then(row);
    });
  }

  private onPerson<T>(values: Record<string, unknown>, then: (row: WorkspaceRowComponent, u: WorkspaceUser) => T | Promise<T>): Promise<T | { refused: string }> {
    return this.onRow<T | { refused: string }>(values, async (row) => {
      await this.openRow(row);
      const u = row.personNamed(values['personId']);
      return 'refused' in u ? u : then(row, u);
    });
  }

  private async prepareProjectSwitch(which: 'signups' | 'invites', enabled: unknown): Promise<Prepared> {
    const gate = projectSwitchControl(this.access);
    if (!gate.enabled) return { refused: `${gate.reason} Nothing was changed.` };
    if (typeof enabled !== 'boolean') return { refused: 'The switch needs enabled to be true or false. Nothing was changed.' };
    const s = this.switches();
    if (!s) return { refused: 'The switches haven\'t loaded yet. Nothing was changed; try again in a moment.' };
    if ((which === 'signups' ? this.signupsOpen(s) : this.invitesOpen(s)) === enabled) {
      return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', [which]: enabled } } };
    }
    const says = which === 'signups'
      ? (enabled ? 'Allow sign-ups. People can create their own account from your app\'s sign-in page.' : 'Stop sign-ups. Your app becomes invitation-only: nobody can create their own account from its sign-in page.')
      : (enabled ? 'Allow invitations across this project. People can be invited by email again, where a workspace allows it.' : 'Stop invitations across this project. Nobody new can be invited by email into any workspace.');
    return { says, confirmLabel: enabled ? 'Turn on' : 'Turn off', run: () => this.ifNothingSaving(() => this.flipProject(which, enabled)) };
  }

  private async prepareMode(mode: unknown): Promise<Prepared> {
    const gate = projectSwitchControl(this.access);
    if (!gate.enabled) return { refused: `${gate.reason} Nothing was changed.` };
    const next = this.modes.find((m) => m === mode);
    if (!next) return { refused: `The mode has to be one of ${this.modes.join(', ')}. Nothing was changed.` };
    if (next === this.mobileAccess()?.mode) return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', mode: next } } };
    const says = next === 'native_only' ? `${MODE_NOTES[next]} ${NATIVE_ONLY_WARNING}` : MODE_NOTES[next];
    return { title: `Make phones open this app: ${MODE_LABELS[next]}`, says, confirmLabel: 'Switch', run: () => this.ifNothingSaving(() => this.applyMode(next)) };
  }

  /** A card stays up while the builder reads it, and the screen's own controls can start a write meanwhile. */
  private async ifNothingSaving(run: () => Promise<SaveOutcome>): Promise<SaveOutcome> {
    if (this.busy()) return { outcome: 'failed', reason: 'Another project switch is still being changed. Nothing new was changed; try again in a moment.' };
    return run();
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.reload(), this.loadDeveloperMode(), this.loadRoleNames()]);
  }

  ngOnDestroy(): void {
    this.navSub.unsubscribe();
  }

  /** Never rejects — same reasoning as `loadDeveloperMode` below: this runs inside `ngOnInit`'s
   *  `Promise.all`, and a role list that fails to load costs only the prettier label, never the page. */
  private async loadRoleNames(): Promise<void> {
    try {
      const roles = await this.roleService.query(1000, false);
      this.roleNames.set(new Map(roles.map((r) => [r.id, r.name || r.id])));
    } catch {
      // Stays empty — every pill falls back to the role id (`roleName`).
    }
  }

  /** The headline numbers above the list — `workspaceTotals` (workspaces.util.ts) makes every call. */
  totals(): WorkspaceTotals {
    return workspaceTotals(this.workspaces());
  }

  /** Rows rise in one after another on arrival; capped, so the fortieth workspace in a long list
   *  isn't still waiting for its turn a second and a half later. */
  rowDelay(index: number): number {
    return 80 + Math.min(index, 10) * 35;
  }

  /** Reacts only to `search` — `access`/every other Input changing here is not this component's
   *  business and must not re-run a search that hasn't actually changed. `firstChange` is skipped:
   *  `search` starts `''`, and an empty term needs no fetch (see `applySearch` itself). */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['search'] && !changes['search'].firstChange) {
      void this.applySearch();
    }
  }

  /**
   * A SEARCH LOADS EVERY WORKSPACE'S PEOPLE. Matching only the rows a builder had already opened
   * would mean the box silently missed the person they were looking for, which is worse than the
   * one extra round of requests this costs. There is no pagination anywhere on this page, so the
   * volume is the same volume they would see by opening every row themselves.
   *
   * `failedIds` (fix-round-1 Critical 2): a workspace whose `listUsers` call failed is NOT the same
   * as a workspace with nobody in it — `a.ok ? a.data.users : []` used to collapse both into the
   * same empty array, which then told `filterWorkspace` "checked, no match" for a workspace that was
   * never actually checked. The caller (`applySearch`) uses this set to keep a failed workspace
   * visible and out of the no-results claim instead.
   */
  private async loadAllPeople(): Promise<{ peopleByWorkspace: Map<string, WorkspaceUser[]>; failedIds: Set<string> }> {
    const failedIds = new Set<string>();
    const entries = await Promise.all(
      this.workspaces().map(async (w): Promise<[string, WorkspaceUser[]]> => {
        const a = await this.ws.listUsers(w.id);
        if (!a.ok) { failedIds.add(w.id); return [w.id, []]; }
        return [w.id, a.data.users];
      }),
    );
    return { peopleByWorkspace: new Map(entries), failedIds };
  }

  /**
   * What `search` (Task 11) does to the list: hides every workspace `filterWorkspace` says is not
   * `visible`, and unions every id it says is `forceOpen` into `openIds` — fix-round-1 #4: an
   * earlier version REPLACED `openIds` wholesale on every term change, which collapsed a row the
   * builder had opened by hand the moment a keystroke changed the term. `forceOpen` means "the
   * search wants this row expanded", never "this row is open" (see `filterWorkspace`'s own comment
   * in workspaces.util.ts) — typing only ever ADDS to what is open; only clearing the box (the
   * branch below) resets it to empty, matching "clearing the box collapses everything again", which
   * governs CLEARING specifically, not every keystroke along the way.
   *
   * `searchSeq` (fix-round-1 Critical 1): the same "newest result wins" idiom as
   * `WorkspaceRowComponent.loadUsers`'s own `loadSeq`. Without it, clearing the box while an older
   * fetch (for an abandoned term) was still in flight let that fetch land AFTER the clear had already
   * reset `filtered`/`openIds`, silently repopulating them with an all-hidden snapshot for a term
   * nobody was searching for any more — no message (the box read empty, so `noResults()` refused to
   * fire), no list, no way back but another keystroke. Bumping the counter at the TOP of every call
   * (including the empty-term branch) means a clear always invalidates whatever was still in flight.
   */
  private async applySearch(): Promise<void> {
    const term = this.search.trim();
    const seq = ++this.searchSeq;
    if (!term) {
      this.searching.set(false);
      this.loadFailed.set(false);
      this.failedIds.set(new Set());
      this.filtered.set(new Map());
      this.openIds.set(new Set());
      return;
    }
    this.searching.set(true);
    const { peopleByWorkspace, failedIds } = await this.loadAllPeople();
    // Stale response — a newer term (or a clear) has taken over since this fetch started. Dropping
    // it here, before touching ANY signal, is also what fixes fix-round-1 Minor 7: only the response
    // that wins ever gets to flip `searching` back off, so two overlapping searches can no longer
    // race each other into leaving "Searching…" stuck on screen.
    if (seq !== this.searchSeq) return;
    this.searching.set(false);
    const next = new Map<string, FilteredWorkspace>();
    const forceOpenIds = new Set<string>();
    for (const w of this.workspaces()) {
      if (failedIds.has(w.id)) {
        // Fix-round-1 Critical 2: stays visible (never silently vanishes), forces nothing open (we
        // don't know whether it matches), and carries no count (there is nothing honest to put in
        // one). `searchLoadErrorMessage()` is what tells the builder this workspace could not be
        // checked at all.
        //
        // `narrowed: false` is fix-round-3 Critical 2, and it is the whole fix: with `narrowed`
        // true this sentinel's empty `people`/`removed` became an empty matched-id Set, and the row
        // — which had loaded its own fourteen people perfectly well — filtered every one of them
        // away and announced "No active match here for this search". The load failing says nothing
        // whatsoever about who matches, so it narrows nothing; `searchFailedFor` below is what tells
        // the row to say so in its own words instead.
        next.set(w.id, { visible: true, forceOpen: false, people: [], removed: [], countLabel: '', narrowed: false });
        continue;
      }
      const f = filterWorkspace(w, peopleByWorkspace.get(w.id) || [], term);
      next.set(w.id, f);
      if (f.forceOpen) forceOpenIds.add(w.id);
    }
    this.filtered.set(next);
    this.failedIds.set(failedIds);
    this.loadFailed.set(failedIds.size > 0);
    if (forceOpenIds.size) {
      const merged = new Set(this.openIds());
      for (const id of forceOpenIds) merged.add(id);
      this.openIds.set(merged);
    }
  }

  /** Defaults to visible: before a search runs (or once it's cleared) `filtered` is empty, and
   *  nothing here should be hidden just because its id has no entry yet. */
  isVisible(id: string): boolean {
    return this.filtered().get(id)?.visible ?? true;
  }

  countLabelFor(id: string): string {
    return this.filtered().get(id)?.countLabel || '';
  }

  /** The ids `filterWorkspace` actually matched for this workspace (active + removed combined),
   *  or `null` to show everybody this row has loaded — the decision itself (and why it must read
   *  `narrowed` rather than test `!entry`) lives in `matchedUserIds` (workspaces.util.ts), spec'd
   *  there. This is a thin caller. */
  matchedUserIdsFor(id: string): Set<string> | null {
    return matchedUserIds(this.filtered().get(id));
  }

  /** Whether THIS workspace is one the current search pass could not load — the row renders
   *  `SEARCH_WORKSPACE_NOT_CHECKED` from it, beside the people it is showing unnarrowed. False
   *  whenever no term is active, for the same reason `searchLoadErrorMessage()` is empty then. */
  searchFailedFor(id: string): boolean {
    return !!this.search.trim() && this.failedIds().has(id);
  }

  /** Step 3's no-results state, lifted into a pure decision (`shouldShowNoResults`,
   *  workspaces.util.ts) per fix-round-1 #7 rather than left as a template `*ngIf` — that decision
   *  is exactly where Critical 1's blank page and Critical 2's false confidence both lived. */
  noResults(): boolean {
    return shouldShowNoResults({
      term: this.search,
      workspaceCount: this.workspaces().length,
      visibleCount: this.workspaces().filter((w) => this.isVisible(w.id)).length,
      anyLoadFailed: this.loadFailed(),
    });
  }

  noResultsMessage(): string {
    return noSearchResultsMessage(this.search);
  }

  /** Fix-round-1 Critical 2's other half: told alongside the results (never in place of them — a
   *  failed workspace stays visible) whenever this search pass could not check everywhere. */
  searchLoadErrorMessage(): string {
    return this.loadFailed() && this.search.trim() ? SEARCH_LOAD_INCOMPLETE : '';
  }

  /** `getHosting()` is viewer+ on the server (`host/enable-hosting.function.ts`'s `status` action),
   *  so this is safe to call unconditionally regardless of `access`.
   *
   *  `try`/`catch` added in fix-round-2 (finding 5): this runs inside `ngOnInit`'s `Promise.all`
   *  alongside `reload()`, and an unhandled rejection here (say, `getAuthToken()` rejecting outside
   *  `hostingCall`'s own try) would reject the whole `Promise.all` with nothing to catch it. The
   *  door still fails closed either way — `developerModeOn` already defaults to `false` — so this
   *  is purely to stop a real failure from surfacing as an unhandled-rejection console warning
   *  instead of whatever `reload()`'s own error handling already shows. */
  private async loadDeveloperMode(): Promise<void> {
    try {
      const hosting = await this.members.getHosting();
      this.developerModeOn.set(!!hosting.developerMode?.on);
    } catch {
      // Stays at its default (false) — see the comment above.
    }
  }

  /**
   * Only `list` and `setProjectSwitch` return fresh state — every other mutation added in Tasks
   * 6-10 returns `{ ok: true }` and nothing else, so THIS is what they call when they finish.
   *
   * `opts.silent` is what a mutation passes: it drives `refreshing` instead of `loading`, so the
   * panel stays mounted (open rows stay open, nothing flashes to the skeleton) while the data
   * underneath it refreshes. The real first load (`ngOnInit`) is the only caller that should ever
   * legitimately show the skeleton.
   *
   * Re-runs `applySearch()` at the end when a term is active — fix-round-1 #5: a mutation changes
   * what `filterWorkspace` would say (removing the one matched person, or renaming a HIDDEN
   * workspace into a match) without ever touching `search` itself, so nothing else would re-run the
   * filter and a stale match/count would sit on screen until the next keystroke. Skipped on an empty
   * term — `applySearch`'s own empty branch would just repeat the reset it already did.
   */
  async reload(opts?: { silent?: boolean }): Promise<void> {
    // Nav 7b T3 review (I1): a workspace note never outlives one visit — taken at the START, before
    // the fetch, so a note left on a FAILED attempt is dropped rather than sitting around to
    // auto-expand a later, unrelated reload (createWorkspace, a project switch, …).
    const pending = this.nav.take('workspace');
    const busy = opts?.silent ? this.refreshing : this.loading;
    busy.set(true);
    const a = await this.ws.list();
    busy.set(false);
    if (!a.ok) { this.error.set(a.error); return; }
    this.error.set('');
    // Was (2026-10-29), before workspaces being deleted sorted last: this.workspaces.set(a.data.workspaces);
    this.workspaces.set(workspacesInPageOrder(a.data.workspaces));
    this.switches.set(a.data.switches);
    this.mobileAccess.set(a.data.mobileAccess ?? null);
    if (this.search.trim()) {
      await this.applySearch();
    }
    // Nav 7b — the note left by `go()` naming a workspace to open (spec Q1).
    if (pending?.kind === 'workspace') this.openIds.update((s) => new Set([...s, pending.id]));
  }

  /** Stopping sign-ups or invitations changes the app for everyone — the owner's alone. */
  get switchControl(): Control {
    return projectSwitchControl(this.access);
  }

  /** `on` MEANS OPEN. The stored column is the opposite (`signupsStopped`) and the server already
   *  inverted it once for `list`/`setProjectSwitch` — inverting it again here would silently reverse
   *  both switches. */
  signupsOpen(s: ProjectSwitches): boolean {
    return !s.signupsStopped;
  }
  invitesOpen(s: ProjectSwitches): boolean {
    return !s.invitesDisabled;
  }

  signupsAuditLine(s: ProjectSwitches): string {
    return auditLine(s.signupsStoppedBy, s.signupsStoppedAt);
  }
  invitesAuditLine(s: ProjectSwitches): string {
    return auditLine(s.invitesDisabledBy, s.invitesDisabledAt);
  }

  /**
   * `on` MEANS OPEN. The control below is labelled "Allow sign-ups"/"Allow invitations", so its
   * checked state IS `on`, and the server stores the opposite (`signups_stopped`/`invites_disabled`)
   * and inverts for us. Sending the stored sense from here would silently reverse both switches.
   */
  async flipProject(which: 'signups' | 'invites', open: boolean): Promise<SaveOutcome> {
    const gate = projectSwitchControl(this.access);
    if (!gate.enabled) return { outcome: 'failed', reason: gate.reason };
    this.busy.set(which);
    const a = await this.ws.setProjectSwitch(which, open);
    this.busy.set('');
    if (!a.ok) { this.error.set(a.error); return { outcome: 'failed', reason: a.error }; }
    this.error.set('');
    this.switches.set(a.data.switches); // this action DOES return fresh state
    return { outcome: 'saved', saved: { [which]: open } };
  }

  /** Selected-but-not-yet-applied mode, so Native app only can warn before it strands anyone. */
  pendingMode = signal<MobileAccessMode | null>(null);
  readonly modeLabels = MODE_LABELS;
  readonly modeNotes = MODE_NOTES;
  readonly nativeOnlyWarning = NATIVE_ONLY_WARNING;
  /** Typed so the template's *ngFor infers the union, not `string` — strictTemplates rejects the
   *  latter when it indexes MODE_LABELS or calls chooseMode (I1, final review). */
  readonly modes: MobileAccessMode[] = ['off', 'pwa_only', 'native_only'];

  chooseMode(mode: MobileAccessMode): void {
    if (!projectSwitchControl(this.access).enabled) return;
    if (mode === this.mobileAccess()?.mode) return;
    // Every other mode applies at once; this one asks first, because it takes the app away from
    // people who already installed it and there is no way to tell them.
    if (mode === 'native_only') { this.pendingMode.set(mode); return; }
    void this.applyMode(mode);
  }

  async applyMode(mode: MobileAccessMode): Promise<SaveOutcome> {
    const gate = projectSwitchControl(this.access);
    if (!gate.enabled) return { outcome: 'failed', reason: gate.reason };
    this.busy.set('mobileAccess');
    const a = await this.ws.setMobileAccessMode(mode);
    this.busy.set('');
    this.pendingMode.set(null);
    if (!a.ok) { this.error.set(a.error); return { outcome: 'failed', reason: a.error }; }
    this.error.set('');
    this.mobileAccess.set(a.data.mobileAccess);
    return { outcome: 'saved', saved: { mode } };
  }

  trackById(_: number, w: Workspace): string {
    return w.id;
  }

  isOpen(id: string): boolean {
    return this.openIds().has(id);
  }

  toggleOpen(id: string): void {
    const next = new Set(this.openIds());
    if (next.has(id)) next.delete(id); else next.add(id);
    this.openIds.set(next);
  }

  /** A single field, one input, above the list — not a modal. Trim + refuse-empty happen here;
   *  the 60-character cap is enforced by the input's own `maxlength` in the template, so the
   *  server's "That name is too long" message is a backstop, not something this method re-checks.
   *  The `creatingWorkspace()` re-entrancy guard matters more here than on rename: a duplicate
   *  rename is just a wasted request, but a duplicate create makes a SECOND workspace, and there
   *  is no delete — it would be permanent. */
  async createWorkspace(): Promise<string | null> {
    if (this.creatingWorkspace()) return null;
    const name = this.newWorkspaceName.trim();
    if (!name) return null;
    this.creatingWorkspace.set(true);
    this.createError.set('');
    const a = await this.ws.createWorkspace(name);
    this.creatingWorkspace.set(false);
    if (!a.ok) { this.createError.set(a.error); return null; }
    this.newWorkspaceName = '';
    const newId = a.data.tenantId;
    // Fix-round-1 #6: safe to unconditionally add `newId` below even mid-search now that `reload()`
    // (just above) re-runs `applySearch()` whenever a term is active — `filtered` already carries a
    // real, freshly-computed entry for `newId` (almost always `visible: false`, since a brand-new
    // empty workspace matches nothing) by the time this runs. Before that fix, `filtered` had no
    // entry at all for an id that didn't exist yet, and `isVisible`'s "no entry → visible" fallback
    // showed the new workspace anyway, forced open, inside what was supposed to be a filtered list.
    await this.reload({ silent: true }); // only `list` returns fresh state — createWorkspace does not
    const next = new Set(this.openIds());
    next.add(newId);
    this.openIds.set(next);
    return newId;
  }

  // ── what the tab holds, and the same button for do_action ───────────────────────────────────────

  holds(): Record<string, unknown> {
    const s = this.switches();
    return {
      newWorkspaceName: this.newWorkspaceName,
      ...(s ? { projectSwitches: { signups: this.signupsOpen(s), invites: this.invitesOpen(s) } } : {}),
      mode: this.mobileAccess()?.mode ?? '',
      invitations: (this.rows?.toArray() ?? []).flatMap((row) => row.invitations().map((i) => ({ workspaceId: row.workspace.id, id: i.id, email: i.email, status: i.status }))),
      workspaces: this.workspaces().map((w) => ({
        id: w.id, name: w.name, isBuilders: w.isBuilders, activeCount: w.activeCount, removedCount: w.removedCount, seatLimit: w.seatLimit,
        invitesEnabled: w.invitesEnabled, addsEnabled: w.addsEnabled, arrivalNotices: arrivalNoticeOn(w),
        open: this.isOpen(w.id), visible: this.isVisible(w.id), beingDeleted: !!deletionReadOnly(w),
      })),
      roles: [...(this.roleNames() ?? new Map<string, string>())].map(([id, name]) => ({ id, name })),
      people: (this.rows?.toArray() ?? []).flatMap((row) => row.peopleHeld()),
      ...Object.assign({}, ...(this.rows?.toArray() ?? []).map((row) => row.formHeld())),
    };
  }

  unsavedHere(): { count: number; name: string } | null {
    if (this.newWorkspaceName.trim()) return { count: 1, name: 'The new workspace' };
    return (this.rows?.toArray() ?? []).map((row) => row.unsavedHere()).find((u) => u) ?? null;
  }

  private async createWorkspaceFor(values: Record<string, unknown>): Promise<ScreenActionAnswer> {
    if (this.creatingWorkspace()) return { refused: 'A workspace is being created. Nothing was changed; try again in a moment.' };
    if (this.newWorkspaceName.trim()) return { refused: 'The builder is typing a new workspace; ask them to finish or clear it first. Nothing was changed.' };
    const name = String(values['newWorkspaceName'] ?? '').trim();
    if (!name) return { refused: 'Enter a name for the workspace. Nothing was changed.' };
    // The input's maxlength is what holds a builder to this, and the server only backstops it.
    if (name.length > 60) return { refused: 'A workspace name holds 60 characters at most. Nothing was changed.' };

    this.newWorkspaceName = name;
    const id = await this.createWorkspace();
    if (!id) {
      this.newWorkspaceName = '';
      return { refused: `${this.createError()} Nothing was changed.` };
    }
    return { done: `Created the workspace ${name}.`, result: { workspace: { id, name } } };
  }
}
